import { hash, toUnitFloat } from "daedalus-core";
import { GROUND_OWNER_TICKS, GROUND_PUBLIC_TICKS, TICK_MS } from "odyssey-shared";

/**
 * How a ground item moves (P2.8-T74, the author's request of 2026-10-10, "Can we make the item kind of float and bounce
 * a little?"): an item that lands while the page watches gives two short hops that settle within half a second, then
 * every item floats gently up and down, eased, on a period of its own phase, so items lying side by side do not bob in
 * step. The phase comes from the item's handle through the shared seeded hash. Every height is a whole number of world
 * pixels, the pixel art's own, and its shadow shrinks a little as it rises. Under reduced motion the item lies still.
 * The motion is one function of time for the item's whole life on the ground: a change of look, the item leaving the
 * view and coming back, or another pooled image drawing it never restarts it.
 */

/**
 * The landing's hops, in order: how long each lasts in milliseconds and how many world pixels it rises at its top.
 */
export const GROUND_HOPS: readonly { readonly ms: number; readonly px: number }[] = [
  { ms: 280, px: 4 },
  { ms: 200, px: 2 },
];

/**
 * How long the landing lasts in milliseconds: its hops end to end.
 */
export const GROUND_LANDING_MS = GROUND_HOPS.reduce((sum, hop) => sum + hop.ms, 0);

/**
 * The float's period in milliseconds: one rise and fall.
 */
export const GROUND_FLOAT_MS = 1800;

/**
 * The float's height from its lowest to its highest point, in world pixels.
 */
export const GROUND_FLOAT_PX = 2;

/**
 * How long the float takes to grow to its full height after the landing, so the first float never jumps.
 */
export const GROUND_FLOAT_RAMP_MS = 600;

/**
 * How much narrower the shadow is drawn for each world pixel the item rises, as a share of its width.
 */
export const GROUND_SHADOW_SHRINK_PER_PX = 0.06;

/**
 * The seed of a ground item's float phase, apart from every other use of the shared hash.
 */
export const GROUND_FLOAT_SEED = 0x464c4f54;

/**
 * A ground item's float phase from its handle: a share of the float's period, the same on every page.
 *
 * @param handle The item's entity handle.
 * @returns The phase, from 0 up to 1.
 */
export function groundFloatPhase(handle: number): number {
  return toUnitFloat(hash(GROUND_FLOAT_SEED, handle));
}

/**
 * How high a ground item is drawn above its point at a moment, in whole world pixels.
 *
 * @param sinceMs Milliseconds since the item began to move: since it landed in view, or since the page first drew it
 *   with the landing skipped (`GROUND_LANDING_MS` earlier).
 * @param phase The item's float phase (`groundFloatPhase`).
 * @returns The lift, 0 or more.
 * @example
 * groundLift(140, 0);
 * // 4: the top of the first hop
 */
export function groundLift(sinceMs: number, phase: number): number {
  let at = Math.max(0, sinceMs);
  for (let i = 0; i < GROUND_HOPS.length; i += 1) {
    const hop = GROUND_HOPS[i] as { readonly ms: number; readonly px: number };
    if (at < hop.ms) {
      const u = at / hop.ms;
      return Math.round(hop.px * 4 * u * (1 - u));
    }
    at -= hop.ms;
  }
  const ramp = Math.min(1, at / GROUND_FLOAT_RAMP_MS);
  const wave = (1 - Math.cos(2 * Math.PI * (at / GROUND_FLOAT_MS + phase))) / 2;
  return Math.round(GROUND_FLOAT_PX * wave * ramp);
}

/**
 * The share of its width a ground item's shadow is drawn at for a lift.
 *
 * @param lift The lift in world pixels.
 * @returns The share, at most 1.
 */
export function groundShadowShare(lift: number): number {
  return 1 - GROUND_SHADOW_SHRINK_PER_PX * lift;
}

/**
 * How long after a session's first drawing an item that comes into view is taken as already lying there, so the items
 * a joining page finds do not all hop at once.
 */
export const GROUND_SETTLED_MS = 2000;

/**
 * How long an item that has left the view is remembered, in milliseconds: an item's whole life on the ground, owned and
 * then public, so the same item coming back at any point of its life carries on where its motion is.
 */
export const GROUND_REMEMBER_MS = (GROUND_OWNER_TICKS + GROUND_PUBLIC_TICKS) * TICK_MS;

/**
 * When each ground item began to move, and its float phase, kept by handle while it is in view and for
 * `GROUND_REMEMBER_MS` after it leaves, so an item that comes back into view carries on mid-float instead of landing
 * again. A handle the server hands to another item, which the snapshot shows as a change of the item's appearance or
 * place (a standing item never changes either) or as an item owned under a handle last seen public (an item never turns
 * owned again), starts again as a new item, so the new item hops when it lands.
 */
export class GroundLandings {
  private readonly since = new Map<number, number>();
  private readonly phases = new Map<number, number>();
  private readonly identities = new Map<number, string>();
  private readonly left = new Map<number, number>();
  private readonly publics = new Set<number>();
  private firstDrawn = -1;

  /**
   * Notes an item in view at a moment, as a new one when its handle is new or now names another item. The same item
   * coming back into view, or turning public, keeps its start and phase. A new item noted once the session's first
   * drawing has settled is seen landing: it hops, and a high tier item the world dropped sounds (P2.8-T77).
   *
   * @param handle The item's handle.
   * @param identity What the snapshot says of the item that does not change while it stands: its appearance and place.
   * @param now The game loop's time in milliseconds.
   * @param open Whether the item is public now.
   * @returns Whether the item is seen landing now: true once for each new item past the settling, never for the same
   *   item again.
   */
  public note(handle: number, identity: string, now: number, open = false): boolean {
    this.begin(now);
    this.left.delete(handle);
    const reopened = !open && this.publics.has(handle);
    if (open) {
      this.publics.add(handle);
    } else {
      this.publics.delete(handle);
    }
    if (this.identities.get(handle) === identity && !reopened) {
      return false;
    }
    const landing = now - this.firstDrawn >= GROUND_SETTLED_MS;
    this.identities.set(handle, identity);
    this.since.set(handle, landing ? now : now - GROUND_LANDING_MS);
    this.phases.set(handle, groundFloatPhase(handle));
    return landing;
  }

  /**
   * Starts the settling clock at a session's first drawing, whether or not it holds a ground item, so the first item
   * that lands after the settling is seen landing; later calls change nothing until `clear` or `settle`.
   *
   * @param now The game loop's time in milliseconds.
   */
  public begin(now: number): void {
    if (this.firstDrawn < 0) {
      this.firstDrawn = now;
    }
  }

  /**
   * Starts the settling clock again, as the character jumps within the session (a respawn, a teleport), so the items
   * around its new place that the page never noted are taken as lying there, not as landing.
   *
   * @param now The game loop's time in milliseconds.
   */
  public settle(now: number): void {
    this.firstDrawn = now;
  }

  /**
   * How high an item is drawn at a moment, in whole world pixels.
   *
   * @param handle The item's handle.
   * @param now The game loop's time in milliseconds.
   * @returns The lift, 0 for a handle never noted.
   */
  public liftOf(handle: number, now: number): number {
    const since = this.since.get(handle);
    return since === undefined ? 0 : groundLift(now - since, this.phases.get(handle) ?? 0);
  }

  /**
   * Notes which items are in view at a moment: an item no longer among them is remembered from the moment it left,
   * and forgotten once it has been gone longer than `GROUND_REMEMBER_MS`.
   *
   * @param present The handles of every ground item in view now.
   * @param now The game loop's time in milliseconds.
   */
  public keep(present: ReadonlySet<number>, now: number): void {
    for (const handle of this.identities.keys()) {
      if (present.has(handle)) {
        continue;
      }
      const left = this.left.get(handle);
      if (left === undefined) {
        this.left.set(handle, now);
      } else if (now - left > GROUND_REMEMBER_MS) {
        this.identities.delete(handle);
        this.since.delete(handle);
        this.phases.delete(handle);
        this.left.delete(handle);
        this.publics.delete(handle);
      }
    }
  }

  /**
   * Forgets every item and the session's first drawing, as a session ends.
   */
  public clear(): void {
    this.identities.clear();
    this.since.clear();
    this.phases.clear();
    this.left.clear();
    this.publics.clear();
    this.firstDrawn = -1;
  }
}
